import clsx from "clsx";
import { useMemo } from "react";
import { SparklesIcon, TagIcon } from "@heroicons/react/24/outline";
import { useAtomValue, useSetAtom } from "jotai";
import {
    applyExistingAccountsAtom,
    draftAtom,
    labelingContextViewAtom,
    selectedFileAtom,
} from "../../state";
import { FloatingMenu, FloatingMenuCommand } from "@/ui/organisms/FloatingMenu";
import { formatDate, useRouter } from "@/utils";
import { ConnectorIcon } from "../ConnectorIcon/ConnectorIcon";
import { ConnectorType } from "@/entities/DataAssociation/File.model";
import {
    FileTree,
    PriorityCategory,
    STATEMENT_TYPE_LABELS,
} from "../../Labeling.types";
import { connectorTreeListAtom } from "../../state/files.state";
import { labelingParamsAtom } from "../../state/params.state";
import {
    FILE_CATEGORY_LABELS,
    STATEMENT_TYPES,
} from "../../labeling.constants";
import { DraftStatus } from "@/entities/DataAssociation/Draft.model";

type ConnectorTreeItemData = {
    sourceConnectorId: string;
    sourceConnectorType: string;
    mostRecent: Record<string, FileTree | null>;
    historical: Record<string, FileTree | null>;
    current: FileTree | null;
};

function isFileInTree(tree: FileTree | null, fileId: string): boolean {
    if (!tree) return false;
    if (tree.id === fileId) return true;
    return tree.labeledFiles?.some((lf) => lf.id === fileId) ?? false;
}

function findFileCategoryKey(
    data: ConnectorTreeItemData[],
    fileId: string,
): PriorityCategory | "current" | null {
    for (const connector of data) {
        if (isFileInTree(connector.current, fileId)) {
            return "current";
        }

        for (const statementType of STATEMENT_TYPES) {
            if (isFileInTree(connector.mostRecent[statementType], fileId)) {
                return "mostRecent";
            }
            if (isFileInTree(connector.historical[statementType], fileId)) {
                return "historical";
            }
        }
    }
    return null;
}

export function FileHeading() {
    const { data: selectedFile, isLoading } = useAtomValue(selectedFileAtom);
    const { data: connectorTreeList, isLoading: isConnectorTreeListLoading } =
        useAtomValue(connectorTreeListAtom);
    const { selectedFileId, selectedDraftId } =
        useAtomValue(labelingParamsAtom);
    const router = useRouter();
    const isReadonly = !router.params.draftId;
    const isDraft = !!selectedDraftId;
    const draft = useAtomValue(draftAtom);
    const setLabelingContextView = useSetAtom(labelingContextViewAtom);
    const applyExistingAccounts = useSetAtom(applyExistingAccountsAtom);
    const categoryKey = useMemo(() => {
        if (!connectorTreeList || !selectedFileId) return null;
        return findFileCategoryKey(connectorTreeList, selectedFileId);
    }, [connectorTreeList, selectedFileId]);

    if (isLoading)
        return (
            <div
                className={clsx(
                    "bg-selected h-[56px] w-full",
                    "animate-pulse rounded",
                )}
            />
        );

    if (!selectedFile) return null;
    const connector = selectedFile.meta?.rawConnector;

    return (
        <div className="flex flex-col gap-5">
            <div
                className={clsx(
                    "w-full",
                    "flex items-start justify-between gap-10",
                )}
            >
                <div className="flex w-full flex-col gap-1">
                    {!!connector && (
                        <div className="flex items-center gap-2">
                            <ConnectorIcon
                                connectorType={
                                    selectedFile.meta?.rawConnector
                                        ?.kind as ConnectorType
                                }
                                large
                            />
                            <div className="flex items-baseline gap-2">
                                <h1 className="pageheading ml-1 break-all">
                                    {STATEMENT_TYPE_LABELS[
                                        selectedFile.documentKind || ""
                                    ] || "Unknown Statement Type"}
                                </h1>
                            </div>
                        </div>
                    )}

                    {!connector && (
                        <div className="flex flex-col gap-1">
                            <p className="pageheading cursor-default">
                                {selectedFile.name}
                            </p>
                        </div>
                    )}

                    {isDraft && (
                        <div className="flex items-center gap-2">
                            <p className="body-s cursor-default text-slate-500">
                                Draft {selectedDraftId.slice(-6)}
                            </p>
                            <span className="h-4 w-px bg-slate-600" />
                            <p className="body-s cursor-default text-slate-500">
                                {draft.data?.status === DraftStatus.COMMITTED
                                    ? "Commited"
                                    : "In Progress"}
                            </p>
                        </div>
                    )}

                    {!isDraft && !isConnectorTreeListLoading && (
                        <div className="flex items-center gap-2">
                            {categoryKey && (
                                <>
                                    <p className="body-s cursor-default text-slate-500">
                                        {FILE_CATEGORY_LABELS[categoryKey]}
                                    </p>
                                    <span className="h-4 w-px bg-slate-600" />
                                </>
                            )}
                            <p className="body-s cursor-default text-slate-500">
                                Created on{" "}
                                {formatDate(selectedFile.createdAt, {
                                    outputFormat: "MMM DD, YYYY",
                                })}
                            </p>
                        </div>
                    )}

                    {!isDraft && isConnectorTreeListLoading && (
                        <div
                            className={clsx(
                                "h-5 w-20 animate-pulse rounded-full bg-slate-600",
                            )}
                        />
                    )}
                </div>

                {isDraft && !isReadonly && (
                    <FloatingMenu>
                        <FloatingMenuCommand
                            icon={<TagIcon className="size-5 cursor-pointer" />}
                            command={{
                                label: "Account management (⌘E)",
                                shortcut: {
                                    key: "e",
                                    mac: "⌘E",
                                    windows: "Ctrl+E",
                                },
                                onClick: () => {
                                    setLabelingContextView({
                                        type: "ACCOUNT_LIST_EDIT",
                                    });
                                },
                            }}
                        />
                        <FloatingMenuCommand
                            icon={
                                <SparklesIcon className="size-5 cursor-pointer" />
                            }
                            command={{
                                label: "Accept account hints (⌘L)",
                                shortcut: {
                                    key: "l",
                                    mac: "⌘L",
                                    windows: "Ctrl+L",
                                },
                                onClick: applyExistingAccounts,
                            }}
                        />
                        <FloatingMenuCommand
                            text="Commit"
                            command={{
                                label: "Commit draft (⌘S)",
                                shortcut: {
                                    key: "s",
                                    mac: "⌘S",
                                    windows: "Ctrl+S",
                                },
                                onClick: () => {
                                    setLabelingContextView({
                                        type: "DRAFT_COMMIT",
                                    });
                                },
                            }}
                        />
                    </FloatingMenu>
                )}
            </div>
        </div>
    );
}
